import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AssessmentResultsRegisterResponse } from '@risksmart-app/trpc/types';
import type { GetAllAssessmentResultsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useIsFeatureVisibleToOrg } from 'src/utils/featureFlags';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps TRPC assessment result data to match the GraphQL query structure
 */
const mapTrpcAssessmentResultToGraphQL = (
  trpcData: AssessmentResultsRegisterResponse
): GetAllAssessmentResultsQuery => {
  return {
    document_assessment_result: trpcData.document_assessment_result.map(
      (dar) => ({
        ...dar,
        __typename: 'document_assessment_result',
        assessments: dar.parents
          .filter((p) => p.ParentType === 'assessment')
          .map((p) => ({ assessment: p.assessment })),
        documents: dar.parents
          .filter((p) => p.ParentType === 'document')
          .map((p) => ({
            document: {
              ...p.document!, // Prefiltered data, it's safe to assert non-null here
              __typename: 'document',
            },
            node: p.node,
          })),
      })
    ),
    obligation_assessment_result: trpcData.obligation_assessment_result.map(
      (oar) => ({
        ...oar,
        __typename: 'obligation_assessment_result',
        assessments: oar.parents
          .filter((p) => p.ParentType === 'assessment')
          .map((p) => ({ assessment: p.assessment })),
        obligations: oar.parents
          .filter((p) => p.ParentType === 'obligation')
          .map((p) => ({
            obligation: {
              ...p.obligation!, // Prefiltered data, it's safe to assert non-null here
              __typename: 'obligation',
            },
          })),
      })
    ),
    risk_assessment_result: trpcData.risk_assessment_result.map((rar) => ({
      ...rar,
      __typename: 'risk_assessment_result',
      assessments: rar.parents
        .filter((p) => p.ParentType === 'assessment')
        .map((p) => ({ assessment: p.assessment })),
      risks: rar.parents
        .filter((p) => p.ParentType === 'risk')
        .map((p) => ({
          risk: {
            ...p.risk!, // Prefiltered data, it's safe to assert non-null here
            __typename: 'risk',
          },
        })),
    })),
  };
};

export const useGetAllAssessmentResultsTRPC = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.resultsRegister.queryOptions(),

    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcError, addNotification, trpcEnabled]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAssessmentResultToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
